import React, { useRef, useState, useEffect, useCallback } from 'react';
import './RevenueTeardown.css';
import { SUPPORTED_CURRENCIES } from './currencies';
import * as FeatherIcon from 'react-feather';


const RevenueTeardown = ({
    enabled,
    onEnabledChange,
    disabled,
    arFilesFound,
    uploadedFile,
    onFileChange,
    existingPdfData,
    showPreview,
    showUploadOption,
    onProceedWithExisting,
    onChooseUpload,
    expectedCurrency,
    onCurrencyChange,
    noCiqId
}) => {
    const fileInputRef = useRef(null);

    // Progress bar state for PDF loading
    const TOTAL_DURATION = 300; // 5 minutes in seconds
    const TICK_INTERVAL = 15; // update every 15 seconds
    const TOTAL_TICKS = TOTAL_DURATION / TICK_INTERVAL; // 20 ticks

    const [progress, setProgress] = useState(0);
    const [timedOut, setTimedOut] = useState(false);
    const tickRef = useRef(0);
    const timerRef = useRef(null);

    const isLoading = showPreview && existingPdfData?.isLoading;
    const pdfReady = showPreview && existingPdfData && !existingPdfData.isLoading && existingPdfData.url;

    // Start / stop the progress timer
    useEffect(() => {
        if (isLoading) {
            // Reset
            tickRef.current = 0;
            setProgress(0);
            setTimedOut(false);

            timerRef.current = setInterval(() => {
                tickRef.current += 1;
                const pct = Math.min((tickRef.current / TOTAL_TICKS) * 100, 100);
                setProgress(pct);

                if (tickRef.current >= TOTAL_TICKS) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    setTimedOut(true);
                }
            }, TICK_INTERVAL * 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isLoading]);

    // When PDF loads successfully, jump to 100%
    useEffect(() => {
        if (pdfReady) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            setProgress(100);
            setTimedOut(false);
        }
    }, [pdfReady]);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/pdf') {
                onFileChange(null, 'Only PDF files are allowed');
                return;
            }
            onFileChange(selectedFile, null);
        }
    };

    const handleRemove = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onFileChange(null, null);
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    // Render PDF preview mode
    if (showPreview && existingPdfData) {
        return (
            <div className="revenue-teardown">
                <div className="revenue-teardown-checkbox-wrapper">
                    <label className="revenue-teardown-label">
                        Revenue Teardown
                    </label>
                    <span className="revenue-teardown-enabled-badge"><FeatherIcon.Check className='comp_icon' width={"16px"} /> Enabled</span>
                </div>

                <div className="revenue-teardown-preview">
                    <div className="revenue-teardown-preview-header">
                        <span className="revenue-teardown-preview-icon"><FeatherIcon.FileText className='comp_icon' width={"16px"} /></span>
                        <span className="revenue-teardown-preview-title">Annual Report Found</span>
                    </div>

                    <div className="revenue-teardown-preview-info">
                        <div className="revenue-teardown-preview-row">
                            <span className="revenue-teardown-preview-label">File Name:</span>
                            <span className="revenue-teardown-preview-value">{existingPdfData.filename}</span>
                        </div>
                        <div className="revenue-teardown-preview-row">
                            <span className="revenue-teardown-preview-label">Type:</span>
                            <span className="revenue-teardown-preview-value">{existingPdfData.fileType || 'Annual Report'}</span>
                        </div>
                        <div className="revenue-teardown-preview-row">
                            <span className="revenue-teardown-preview-label">Filing Date:</span>
                            <span className="revenue-teardown-preview-value">{formatDate(existingPdfData.filingDate)}</span>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    {(isLoading || pdfReady) && (
                        <div className="revenue-teardown-progress-wrapper">
                            <div className="revenue-teardown-progress-header">
                                <span className="revenue-teardown-progress-label">
                                    {pdfReady
                                        ? 'Document loaded successfully'
                                        : timedOut
                                            ? 'Loading timed out — you may upload your own PDF'
                                            : 'Fetching document preview…'}
                                </span>
                                <span className="revenue-teardown-progress-pct">{Math.round(progress)}%</span>
                            </div>
                            <div className="revenue-teardown-progress-track">
                                <div
                                    className={`revenue-teardown-progress-fill${pdfReady ? ' complete' : timedOut ? ' timed-out' : ''
                                        }`}
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Embedded PDF Viewer */}
                    <div className="revenue-teardown-preview-viewer">
                        {existingPdfData.isLoading ? (
                            <div className="revenue-teardown-preview-loading">
                                <div className="revenue-teardown-spinner"></div>
                                <span>Loading PDF preview...</span>
                            </div>
                        ) : existingPdfData.url ? (
                            <>
                                <iframe
                                    src={existingPdfData.url}
                                    title="PDF Preview"
                                    className="revenue-teardown-pdf-iframe"
                                />
                                <a
                                    href={existingPdfData.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="revenue-teardown-preview-link"
                                >
                                    <FeatherIcon.Link className='comp_icon' width={"16px"} /> Open PDF in new tab
                                </a>
                            </>
                        ) : (
                            <div className="revenue-teardown-preview-no-preview">
                                <span>                            <FeatherIcon.FileText className='comp_icon' width={"16px"} />
                                </span>
                                <span>PDF preview not available</span>
                            </div>
                        )}
                    </div>

                    <div className="revenue-teardown-preview-actions">
                        <button
                            type="button"
                            className="revenue-teardown-btn revenue-teardown-btn-primary"
                            onClick={onProceedWithExisting}
                            disabled={existingPdfData.isLoading}
                        >
                            <FeatherIcon.Check className='comp_icon' width={"16px"} />
                            Proceed with this PDF
                        </button>
                        <button
                            type="button"
                            className="revenue-teardown-btn revenue-teardown-btn-secondary"
                            onClick={onChooseUpload}
                            disabled={existingPdfData.isLoading && !timedOut}
                        >
                            <FeatherIcon.ArrowUp className='comp_icon' width={"16px"} /> Upload Different PDF
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="revenue-teardown">
            <div className="revenue-teardown-checkbox-wrapper">
                <label
                    htmlFor="revenueTeardown"
                    className={`revenue-teardown-label ${disabled ? 'disabled' : ''}`}
                >
                    Revenue Teardown
                </label>
                <input
                    type="checkbox"
                    id="revenueTeardown"
                    className="revenue-teardown-checkbox"
                    checked={enabled}
                    onChange={(e) => onEnabledChange(e.target.checked)}
                    disabled={disabled || noCiqId}
                />
            </div>

            <p className="revenue-teardown-description">
                Enable to include revenue teardown analysis in the workflow (optional)
            </p>

            {noCiqId && (
                <div className="revenue-teardown-warning">
                    Revenue teardown is not possible as company financials and documents is not available.
                </div>
            )}
            {enabled && (
                <div className="revenue-teardown-currency">
                    <label className="revenue-teardown-currency-label">
                        Reporting Currency
                    </label>

                    <select
                        className="revenue-teardown-currency-select"
                        value={expectedCurrency}
                        onChange={(e) => onCurrencyChange(e.target.value)}
                        disabled={disabled}
                    >
                        {SUPPORTED_CURRENCIES.map(({ code, name }) => (
                            <option key={code} value={code}>
                                {name} ({code})
                            </option>
                        ))}
                    </select>
                </div>
            )}


            {enabled && !showUploadOption && arFilesFound && (
                <div className="revenue-teardown-enabled">
                    <FeatherIcon.Check className='comp_icon' width={"16px"} /> Revenue teardown will be processed using Financial Details company
                </div>
            )}

            {/* PDF Upload - shown when enabled AND (AR files not found OR showUploadOption is true) */}
            {enabled && (showUploadOption || !arFilesFound) && (
                <div className="revenue-teardown-upload">
                    <div className="revenue-teardown-upload-header">
                        <span className="revenue-teardown-upload-title">
                            {!arFilesFound
                                ? 'Annual Report not found. Please upload PDF for Revenue Teardown.'
                                : 'Upload a new PDF to replace the existing one.'}
                        </span>
                    </div>

                    <div className="revenue-teardown-upload-input-wrapper">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            className="revenue-teardown-upload-input"
                            onChange={handleFileChange}
                            disabled={disabled}
                        />

                        {!uploadedFile ? (
                            <button
                                type="button"
                                className="revenue-teardown-upload-button"
                                onClick={handleButtonClick}
                                disabled={disabled}
                            >
                                Choose PDF File
                            </button>
                        ) : (
                            <div className="revenue-teardown-upload-file-info">
                                <span>📄</span>
                                <span className="revenue-teardown-upload-file-name">{uploadedFile.name}</span>
                                <button
                                    type="button"
                                    className="revenue-teardown-upload-file-remove"
                                    onClick={handleRemove}
                                    disabled={disabled}
                                >
                                    ×
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RevenueTeardown;